/**
 * @fileoverview The share studio's story: what is being shared, the
 * photographs offered and the ones going out (up to the server's
 * limits.SHARE_MAX_PHOTOS, in order), the picture in view, the screen that is
 * up, the sharer's choices (kept on the device, shareStyle), the crops, the
 * handle and the dates, and the share in progress kept on the device so a
 * reloaded tab puts the sheet back (shareDraft). Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105); nothing in
 * it changed.
 *
 * Where: SharePlantDialog, and the hooks and bands it hands the story to
 * (features/share/studio).
 * API: GET /gardens/mine (the gardener's Grow address, for the signature);
 * GET /garden-place (useGardenPostcode: whose garden it is, and for a member
 * the owner's handles and address).
 * Settings: limits.SHARE_MAX_PHOTOS (lib/growSettings); the account's social
 * links (the handle); the gardener's Public pages date choice.
 *
 * In someone else's garden (B37; owner, 2026-10-20: "sharing should only have
 * the handle of the inviter not invitee available") the handle and the
 * signature are the garden owner's, as the server sends them (ownerShare),
 * never the member's own; and the share carries no link (tending: only the
 * owner makes one, useShareLink).
 * Words (en/es): plants.share.dayOne, plants.share.days.
 *
 * @module useShareStory
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useShareHandles } from '@/features/share/useShareHandles';
import { formatDateParts, parseDate } from '@/shared/utils/dateUtils';
import type { PlantPhoto } from '@/shared/types/photo';
import { frameCount, layoutAvailable, TEXT_SCALES, type ShareElements, type ShareLayout } from '@/features/share/frames/frameOptions';
import { refitFocus } from '@/shared/lib/photoFit';
import { growLimit, useGrowLimits } from '@/shared/lib/growSettings';
import { defaultShareStyle, readShareStyle, startingElements, writeShareStyle, type ShareStyle } from '@/features/plant-list/lib/shareStyle';
import { clearShareDraft, readShareDraft, shareDraftKey, writeShareDraft, type ShareStep } from '@/features/plant-list/lib/shareDraft';
import { plantSubject } from '@/shared/api/subject';
import { MY_GARDEN_QUERY_KEY, MY_GARDEN_STALE_MS } from '@/config/tunables/garden';
import { getMyGarden } from '@/shared/api/gardens';
import { isUnderReview } from '@/shared/lib/underReview';
import { storyOrder } from './shareOrder';
import {
  CENTRE, COLLAGE_ID, SEASON_CARD_ID, type Focus, type Selection, type SharePlantDialogProps,
} from './shareStudioTypes';

/**
 * A draft's crops as the story keeps them: by photograph. A draft from the build
 * before keeps one per size ("12:square"); the last of a photograph's is kept.
 *
 * @param saved - The draft's crops.
 * @returns The crops by photograph id.
 */
function focusFromDraft(saved: Record<string, Focus> | undefined): Record<string, Focus> {
  const out: Record<string, Focus> = {};
  for (const [key, value] of Object.entries(saved ?? {})) out[key.split(':')[0]] = value;
  return out;
}

/**
 * The story the share sheet tells, and every choice the sharer makes on it.
 *
 * @param props - The dialog's props (SharePlantDialogProps).
 * @returns The story, the picture in view, the screen, the choices and their setters, for the dialog, its hooks and its bands.
 */
// The story's type is what it returns (ShareStory, below): sixty-odd values the dialog, its hooks and its bands read.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useShareStory(props: SharePlantDialogProps) {
  const {
    isOpen, onClose, plantObjId, subject: subjectProp, photos, startPhotoId = null, startPhotoIds, season,
  } = props;

  /** What is being shared: the location given, or else the plant. */
  const subject = React.useMemo(() => subjectProp ?? plantSubject(plantObjId), [subjectProp, plantObjId]);
  /** A location's share: its own words where a plant's would be wrong ("the plant isn't public"). */
  const isLocation = subject.kind === 'location';
  /** Where the share in progress is kept on the device (shareDraft): a season's by its year. */
  const draftKey = season ? shareDraftKey('season', season.year) : shareDraftKey(subject.kind, subject.id);
  /** Closes the sheet and forgets the share in progress: every way out of the sheet comes through here. */
  const closeSheet = (): void => {
    clearShareDraft(draftKey);
    onClose();
  };
  /*
   * Leaving the page in the app (another tab of Grow, then back to the plant)
   * forgets the share too: only a reload of the whole tab, which Android does
   * behind Instagram and which runs no clean-up, puts the sheet back (owner,
   * 2026-10-12: the sheet came back up after sharing and going back to Plants).
   */
  const draftKeyNow = React.useRef(draftKey);
  draftKeyNow.current = draftKey;
  /** Whether the sheet is open now, read as it unmounts: a closed sheet has nothing of its own to forget. */
  const openNow = React.useRef(isOpen);
  openNow.current = isOpen;
  React.useEffect(() => () => { if (openNow.current) clearShareDraft(draftKeyNow.current); }, []);
  const { reactTranslate: t } = useTranslation();
  /** The most photos one share carries: the server's (limits.SHARE_MAX_PHOTOS); no cap is drawn before the app has heard it. */
  const maxPhotos = useGrowLimits().SHARE_MAX_PHOTOS;
  /**
   * Whose garden it is (the server's) and the handles offered: in their own, theirs; a member's, the owner's, with the
   * owner's address and no link (B37, useShareHandles).
   */
  const { ownGarden, tending, links, ownerSlug } = useShareHandles();

  /** The gardener's own garden address, for the collage's small credit ("@amy"). */
  const { data: myGarden } = useQuery({ queryKey: [MY_GARDEN_QUERY_KEY], queryFn: getMyGarden, staleTime: MY_GARDEN_STALE_MS, enabled: isOpen });
  /** The garden's address: the person's own in their garden, the owner's in someone else's (none until known). */
  const slug = ownGarden ? myGarden?.slug : ownerSlug;
  /** The signature on the first picture: the Grow address (this app's own address, then /@name); none before there is one. */
  const address = slug ? `${window.location.host}/@${slug}` : null;
  /** The sharer's choices (size, layout, ground, what is printed, text size), kept on the device. */
  const [style, setStyle] = React.useState<ShareStyle>(defaultShareStyle);
  /**
   * Which parts of the printed date show (owner, 2026-10-14: "january 2024 or
   * january 21 or january 21 2024"): the Day and Year chips here, else the
   * gardener's Profile > Public pages choice, else every part.
   */
  const dateDay = style.dateDay ?? myGarden?.dates.day ?? true;
  const dateYear = style.dateYear ?? myGarden?.dates.year ?? true;

  // Oldest first, because that is the direction growth reads in: a plant's is the card's order turned round (the server's,
  // never sorted again here, E20), a season's by the day taken; a location's as given, its own pictures then its plants'. A photo being reviewed (the
  // safety check flagged it) is never offered.
  /** A season's card, standing among the photographs so it can be picked, shown and moved like one. */
  const cardPhoto = React.useMemo<PlantPhoto | null>(
    () => (season ? { photo_id: SEASON_CARD_ID, filename: '', date_taken: '', date_uploaded: '' } : null),
    [season],
  );
  // A season's card heads its photographs.
  const ordered = React.useMemo(() => {
    const shareable = photos.filter((photo) => !isUnderReview(photo));
    const sorted = storyOrder(shareable, isLocation ? 'location' : season ? 'season' : 'plant');
    return cardPhoto ? [cardPhoto, ...sorted] : sorted;
  }, [photos, isLocation, season, cardPhoto]);
  /** The photographs alone, without a season's card: what the layouts arrange. */
  const photosOnly = React.useMemo(() => (cardPhoto ? ordered.slice(1) : ordered), [ordered, cardPhoto]);
  /**
   * Whether a photograph is the season's card.
   *
   * @param photo - The photograph, if any.
   */
  const isCard = (photo: PlantPhoto | undefined | null): boolean => Boolean(season) && photo?.photo_id === SEASON_CARD_ID;
  /** A location's share: what its own pictures print, and what its plants' photos print, switched apart. */
  const [coverElements, setCoverElements] = React.useState<ShareElements>(() => startingElements('cover'));
  /** A location's share: what its plants' photos print. */
  const [plantElements, setPlantElements] = React.useState<ShareElements>(() => startingElements('plantPhoto'));
  /** Each photograph by its id, for the story's ids. */
  /** The collage's stand-in slide (layout collage), found by its id like a photograph. */
  const collagePhoto = React.useMemo<PlantPhoto>(
    () => ({ photo_id: COLLAGE_ID, filename: '', date_taken: '', date_uploaded: '' }),
    [],
  );
  /** Every photograph offered, and the collage's stand-in, by id. */
  const photoById = React.useMemo(
    () => new Map([...ordered, collagePhoto].map((p) => [p.photo_id, p])), [ordered, collagePhoto]
  );

  /*
   * The story going out, in the order it goes (up to ten photographs), and the
   * picture being worked on. The strip draws from this alone, never from the
   * pictures being made, so a tap shows the moment it lands.
   */
  const [selection, setSelection] = React.useState<Selection>({ picked: [], shown: null });
  const { picked, shown } = selection;
  /**
   * Puts a photograph in view, leaving the story as it is.
   *
   * @param id - The photograph.
   */
  const setShown = React.useCallback(
    (id: number) => setSelection((current) => ({ ...current, shown: id })), []
  );
  /** One line on every picture, or a line each. */
  const [sameWords, setSameWords] = React.useState(true);
  /** The one line, when every picture has the same. It also goes with the share as its words. */
  const [sharedWords, setSharedWords] = React.useState('');
  /** Each photograph's own line, by its id, when they differ. */
  const [perWords, setPerWords] = React.useState<Record<number, string>>({});
  /** Where each photograph sits in its frame, by its id: one crop that is refitted to whatever size and layout are chosen (B86). */
  const [focus, setFocus] = React.useState<Record<string, Focus>>({});
  /** Which of the six screens is up. Pick first. */
  const [step, setStep] = React.useState<ShareStep>('pick');

  /** A share, copy or save that failed, and why. */
  const [error, setError] = React.useState<string | null>(null);
  /** What was saved or copied, said under the picture. */
  const [saved, setSaved] = React.useState<string | null>(null);

  /*
   * A share tells a story: up to ten photographs, in order. It starts with the
   * one the card was showing, or the newest, and Pick's grid of every
   * photograph the plant has builds the rest: tap one to add it, and its
   * number says where it falls.
   *
   * Once per opening. A card hands over a new list of photographs whenever it
   * draws again (a location's is put together on every draw), and starting
   * over on each one threw away the photographs the gardener had just tapped.
   * It waits for the photographs when the sheet opens before they arrive.
   */
  const begun = React.useRef(false);
  /** Set once the sheet has started (or been put back): from then on, what it holds is kept (shareDraft). */
  const resumable = React.useRef(false);
  React.useEffect(() => {
    if (!isOpen) {
      begun.current = false;
      resumable.current = false;
      return;
    }
    if (begun.current || ordered.length === 0) return;
    begun.current = true;
    const starts = (startPhotoIds ?? []).filter((id) => photoById.has(id)).slice(0, growLimit('SHARE_MAX_PHOTOS'));
    if (starts.length > 0) {
      setSelection({ picked: starts, shown: starts[0] });
    } else {
      const start = startPhotoId !== null && photoById.has(startPhotoId) ? startPhotoId : null;
      const first = start ?? ordered[ordered.length - 1]?.photo_id ?? null;
      setSelection({ picked: first === null ? [] : [first], shown: first });
    }
    setStyle(readShareStyle());
    setSameWords(true);
    setSharedWords('');
    setPerWords({});
    setFocus({});
    setStep('pick');
    setError(null);
    setSaved(null);
    // A share in progress for the same thing (the tab was reloaded while the gardener was in another app): back where it was.
    const draft = readShareDraft(draftKey);
    const kept = draft ? draft.selection.picked.filter((id) => photoById.has(id)).slice(0, growLimit('SHARE_MAX_PHOTOS')) : [];
    if (draft && kept.length > 0) {
      setSelection({ picked: kept, shown: draft.selection.shown !== null && photoById.has(draft.selection.shown) ? draft.selection.shown : kept[0] });
      // No words are printed any more: a draft's are not put back on the pictures.
      setFocus(focusFromDraft(draft.focus));
      setStep(draft.step);
      if (draft.coverElements) setCoverElements(draft.coverElements);
      if (draft.plantElements) setPlantElements(draft.plantElements);
    }
    resumable.current = true;
    // The card's photo is read once, as the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ordered]);

  // The share in progress, kept as it changes, so a reloaded tab can put the sheet back (shareDraft).
  React.useEffect(() => {
    if (!isOpen || !resumable.current || selection.picked.length === 0) return;
    writeShareDraft({
      subject: draftKey, selection, sameWords, sharedWords, perWords, focus, step,
      ...(isLocation ? { coverElements, plantElements } : {}),
    });
  }, [isOpen, draftKey, selection, sameWords, sharedWords, perWords, focus, step, isLocation, coverElements, plantElements]);

  /** The photographs going out, in the order they go. */
  const story = React.useMemo(
    () => picked.map((id) => photoById.get(id)).filter((p): p is PlantPhoto => Boolean(p)),
    [picked, photoById]
  );
  /** The photograph in view: the one chosen, or the story's first, or the newest. */
  const shownPhoto = (shown !== null ? photoById.get(shown) : undefined) ?? story[0] ?? ordered[ordered.length - 1];
  /** The season's card is in view. */
  const shownIsCard = isCard(shownPhoto);
  /** A season's card is going out. It always goes first, whenever it was tapped in. */
  const cardIn = Boolean(season) && picked.includes(SEASON_CARD_ID);
  /** The story's photographs, without a season's card. */
  const photoStory = React.useMemo(
    () => (season ? story.filter((p) => p.photo_id !== SEASON_CARD_ID) : story),
    [story, season],
  );
  // Peek and Before & after need a second photograph from the plant.
  const layout: ShareLayout = layoutAvailable(style.layout, photosOnly.length) ? style.layout : 'full';
  /**
   * What the pictures are made from. Every layout makes a picture of each
   * photograph in the story; Before & after makes one, from the story's first
   * and last, or with a story of one, the plant's first photograph and it.
   * A season's card is not among them: it is made apart, and goes first.
   */
  const set = React.useMemo<PlantPhoto[]>(() => {
    if (layout !== 'ba') return photoStory;
    if (photoStory.length >= 2) return [photoStory[0], photoStory[photoStory.length - 1]];
    const inView = shownPhoto && !(season && shownPhoto.photo_id === SEASON_CARD_ID) ? shownPhoto : undefined;
    const one = photoStory[0] ?? inView;
    const first = photosOnly[0];
    if (!one || !first) return [];
    return [first, first.photo_id === one.photo_id ? photosOnly[photosOnly.length - 1] : one];
  }, [layout, photoStory, shownPhoto, photosOnly, season]);
  /** A story of one, in Peek, shows the plant's next photograph at the edge. */
  const peekPhoto = React.useMemo(() => {
    if (layout !== 'peek' || photoStory.length !== 1) return undefined;
    const at = photosOnly.indexOf(photoStory[0]);
    return photosOnly[at + 1] ?? photosOnly[at - 1];
  }, [layout, photoStory, photosOnly]);
  /** The set's photographs by id, in order. */
  const order = set.map((p) => p.photo_id);
  /** The set, its peeked photograph and a season's card, as one key. */
  const selectionKey = `${order.join(',')}|${peekPhoto?.photo_id ?? ''}|${cardIn ? 'card' : ''}`;
  /** A collage of the set goes out first (layout collage, two photographs or more). */
  const collageIn = layout === 'collage' && set.length >= 2;
  /** The collage's slide is in view. */
  const shownIsCollage = shownPhoto?.photo_id === COLLAGE_ID;
  /** How many pictures the set makes: a season's card, then the photographs' (a collage's one more). */
  const count = (cardIn ? 1 : 0) + frameCount(layout, set.length);
  /** Nothing is going out: no photograph, and no season's card. */
  const nothingToSend = set.length === 0 && !cardIn;
  /**
   * The pictures going out, a photograph standing for each, in the order
   * they go: a season's card first, then the set (Before & after's one
   * picture stood for by its last photograph). The dots and arrows step
   * through these.
   */
  const slides = React.useMemo<PlantPhoto[]>(
    () => [
      ...(cardIn && cardPhoto ? [cardPhoto] : []),
      ...(collageIn ? [collagePhoto] : []),
      ...(layout === 'ba' ? set.slice(-1) : set),
    ],
    [cardIn, cardPhoto, collageIn, collagePhoto, layout, set],
  );

  /**
   * Tapping a photograph on Pick: one not in the story joins it at the end
   * (a season's card at the front, where it goes out), numbered; one in the
   * story leaves it. Decided from the story as it stands when the tap
   * lands, so quick taps each count once and in order.
   *
   * @param id - The photograph tapped.
   */
  const pickPhoto = (id: number): void => {
    setSelection((current) => {
      const { picked: now } = current;
      if (now.includes(id)) {
        const rest = now.filter((x) => x !== id);
        return { picked: rest, shown: rest[0] ?? null };
      }
      const most = growLimit('SHARE_MAX_PHOTOS');
      if (most !== undefined && now.length >= most) return current;
      return { picked: season && id === SEASON_CARD_ID ? [id, ...now] : [...now, id], shown: id };
    });
  };

  /** Where the text size stands among TEXT_SCALES, for the A− and A+ buttons. */
  const scaleStep = Math.max(0, (TEXT_SCALES as readonly number[]).indexOf(style.textScale));

  /**
   * Changes some of the choices, and keeps them for next time.
   *
   * @param patch - The choices that change.
   */
  const update = React.useCallback((patch: Partial<ShareStyle>) => {
    setStyle((current) => {
      const next = { ...current, ...patch };
      writeShareStyle(next);
      return next;
    });
  }, []);
  /**
   * A step larger or smaller lettering, from the size as it stands, so two quick taps are two steps.
   *
   * @param delta - 1 larger, -1 smaller.
   */
  const stepText = (delta: number): void => {
    setStyle((current) => {
      const at = Math.max(0, (TEXT_SCALES as readonly number[]).indexOf(current.textScale));
      const next = { ...current, textScale: TEXT_SCALES[Math.min(TEXT_SCALES.length - 1, Math.max(0, at + delta))] };
      writeShareStyle(next);
      return next;
    });
  };
  /**
   * Turns one printed thing on or off, from the choices as they stand, so two
   * quick taps on different chips both count.
   *
   * @param key - What is printed.
   * @param on - Print it or not.
   */
  const setElement = (key: keyof ShareElements, on: boolean): void => {
    setStyle((current) => {
      const next = { ...current, elements: { ...current.elements, [key]: on } };
      writeShareStyle(next);
      return next;
    });
  };

  /* ------------------------------------------------------------ words */
  /**
   * A photograph's line: the one line for all, or its own.
   *
   * @param photo - The photograph.
   */
  const wordsFor = React.useCallback(
    (photo: PlantPhoto) => (sameWords ? sharedWords : (perWords[photo.photo_id] ?? '')),
    [sameWords, sharedWords, perWords]
  );
  /** Every line as it stands, as one key. */
  const wordsKey = sameWords ? `all:${sharedWords}` : JSON.stringify(perWords);

  /* ------------------------------------------------------------ focus */
  /**
   * A photograph's crop is kept once, whatever the size or layout (B86): it is the
   * gardener's centre and zoom, refitted to the slot each size and layout makes.
   *
   * @param id - The photograph.
   * @returns Its key in `focus`.
   */
  const focusKeyOf = (id: number) => String(id);
  /**
   * Where a photograph was cropped, as the gardener left it (the shape it was made in
   * is in `a`); the centre until it is cropped. The pictures are drawn from this and
   * refit it themselves (drawCover).
   *
   * @param photo - The photograph.
   */
  const focusFor = React.useCallback(
    (photo: PlantPhoto): Focus => focus[String(photo.photo_id)] ?? CENTRE,
    [focus]
  );
  /** Each photograph's width over its height, told as the pictures read them (not state: nothing redraws for it). */
  const shapes = React.useRef(new Map<number, number>());
  /**
   * Notes a photograph's shape, as the frames read it.
   *
   * @param photoId - The photograph.
   * @param aspect - Its width over its height.
   */
  const noteShape = React.useCallback((photoId: number, aspect: number) => { shapes.current.set(photoId, aspect); }, []);
  /**
   * A photograph's crop fitted to the slot it is now cropped in: what Move & zoom
   * and a drag on the picture start from. Unrefitted until the photograph's shape is known.
   *
   * @param photo - The photograph.
   * @param slot - The slot's width over its height.
   */
  const focusIn = (photo: PlantPhoto, slot: number): Focus => {
    const shape = shapes.current.get(photo.photo_id);
    return shape ? refitFocus(focusFor(photo), { width: shape, height: 1 }, slot) : focusFor(photo);
  };
  /**
   * Keeps a crop the gardener made in a slot of this shape.
   *
   * @param photo - The photograph.
   * @param next - Where it sits now.
   * @param slot - The slot's width over its height.
   */
  const saveFocus = (photo: PlantPhoto, next: Focus, slot: number): void => {
    setFocus((current) => ({ ...current, [focusKeyOf(photo.photo_id)]: { ...next, a: slot } }));
  };
  /** Every crop as it stands, as one key. */
  const focusKey = JSON.stringify(focus);

  /* ----------------------------------------------------------- handle */
  /** The link printed as the handle: the one chosen, else their first. */
  const chosenLink = React.useMemo(() => {
    if (links.length === 0) return null;
    return links.find((link) => link.key === style.handle) ?? links[0];
  }, [links, style.handle]);
  /** The handle as the frames print it, or null without a link. */
  const handle = React.useMemo(
    () => (chosenLink ? { text: chosenLink.handle, place: chosenLink.key } : null),
    [chosenLink]
  );
  /** The handle, as one key. */
  const handleKey = handle ? `${handle.place}:${handle.text}` : '';

  /**
   * A photograph's date as printed on the frame.
   *
   * @param iso - The day it was taken.
   */
  const formatOne = React.useCallback((iso: string) => formatDateParts(parseDate(iso), { day: dateDay, year: dateYear }, 'long'), [dateDay, dateYear]);
  /**
   * "41 days", for the before-and-after band.
   *
   * @param days - The days between the two photographs.
   */
  const formatSpan = React.useCallback(
    (days: number) => (days === 1 ? t('plants.share.dayOne') : t('plants.share.days', { count: days })),
    [t]
  );

  return {
    subject, isLocation, draftKey, closeSheet, t, maxPhotos, address, tending, style, dateDay, dateYear, cardPhoto, ordered, photosOnly, isCard, coverElements, setCoverElements, plantElements, setPlantElements, collagePhoto, photoById, selection, setSelection, picked, shown, setShown, sameWords, sharedWords, perWords, focus, setFocus, step, setStep, error, setError, saved, setSaved, story, shownPhoto, shownIsCard, cardIn, photoStory, layout, set, peekPhoto, order, selectionKey, collageIn, shownIsCollage, count, nothingToSend, slides, pickPhoto, scaleStep, update, stepText, setElement, wordsFor, wordsKey, focusKeyOf, focusFor, focusIn, saveFocus, noteShape, focusKey, links, chosenLink, handle, handleKey, formatOne, formatSpan,
  };
}

/** What useShareStory hands the dialog, its hooks and its bands. */
export type ShareStory = ReturnType<typeof useShareStory>;
